import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DoneIcon, WarningIcon } from "../assets/registtyIcons/icons";
import { resolveIcon } from "../utils/resolveIcon";
import ErrorBox from "./ErrorBox";

const FilesUpload = ({
  file,
  onFileChange,
  onDeleteFile,
  label,
  isMand = false,
  accept = ".pdf,application/pdf",
}) => {
  const { t } = useTranslation("general");
  const [inputKey, setInputKey] = useState(Date.now());
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

  const validateFileSize = (selectedFile) => {
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError(t("fileSizeTooLarge"));
      return false;
    }
    setError(null);
    return true;
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (validateFileSize(selectedFile)) {
        onFileChange(selectedFile);
      }
    }
  };

  const handleDelete = () => {
    onDeleteFile();
    setInputKey(Date.now()); // Reset input
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      // Check if file type matches accept attribute
      const acceptedTypes = accept.split(",").map((type) => type.trim());
      const fileExtension =
        "." + droppedFile.name.split(".").pop().toLowerCase();
      const mimeType = droppedFile.type;

      const isAccepted = acceptedTypes.some(
        (type) => type === mimeType || type === fileExtension,
      );

      if (isAccepted) {
        if (validateFileSize(droppedFile)) {
          onFileChange(droppedFile);
        }
      } else {
        alert(t("onlyPdfFilesAllowed"));
      }
    }
  };

  return (
    <div className="c-files-upload">
      {error && (
        <div className="u-margin--top_200">
          <ErrorBox>{error}</ErrorBox>
        </div>
      )}
      {!file && (
        <>
          {isMand && (
            <span className="u-color_success u-font-size_small o-row o-row--vertical-align_center o-row--gap-size_200 u-margin--top_200">
              <WarningIcon width={20} height={20} />
              {t("mandatoryFile")}
            </span>
          )}
          <input
            key={inputKey}
            accept={accept}
            type="file"
            onChange={handleFileSelect}
            className="c-files-upload fileInput"
            id={`pdf-upload-${label}`}
          />
          <label
            htmlFor={`pdf-upload-${label}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={isDragging ? "dragging" : ""}
          >
            <p className="c-files-upload customFileInput">
              <img src={resolveIcon("uploadIconPrimary")} alt="Upload" />
              <span>{label || t("uploadDocument")}</span>
              <span className="drag-hint">{t("uploadOrDragDrop")}</span>
            </p>
          </label>
        </>
      )}

      {file && (
        <div className="o-row o-row--vertical-align_center o-row--gap-size_200">
          <div className="c-files-upload fileName u-width_fit-content">
            <span>
              {t("fileName")}: {file.name?.slice(0, 50)}
            </span>
            <button
              type="button"
              className="c-files-upload deleteFileButton"
              onClick={handleDelete}
            >
              <img src={resolveIcon("deleteCircled")} alt="Delete" />
            </button>
          </div>
          <span>
            <DoneIcon width={30} height={30} />
          </span>
        </div>
      )}
    </div>
  );
};

export default FilesUpload;
